import { useState, useEffect } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  statusStyles,
  statusDotStyles,
  formatDateTime,
} from "../../utils/styles";
import {
  faBuilding,
  faCalendarAlt,
  faCheckCircle,
  faClock,
  faEdit,
  faEnvelope,
  faIdBadge,
  faPhone,
  faSave,
  faTimes,
  faUser,
  faUserCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FaRegAddressCard } from "react-icons/fa";
import { toastError, toastSuccess } from "../../utils/toast";
import { useAuth } from "../../hooks";
import { userService } from "../../services/service";

const MyProfile = () => {
  const { user, userAccount, refreshUserAccount } = useAuth();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch user account data
  useEffect(() => {
    document.title = "My Profile - Shop Kiganjani";

    const loadProfile = async () => {
      if (!user?.username) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const account = userAccount ?? (await refreshUserAccount());

        setCustomer({
          id: account?.id || null,
          userId: account?.userId || null,
          customerRegNo: user.username || "N/A",
          fullName: account?.full_name || "N/A",

          emailAddress: account?.email || user.email || "N/A",
          phoneNumber: account?.phone || "N/A",
          position: user.role || "N/A",
          status: account?.status || "ACTIVE",
          createdAt: account?.created_at || new Date().toISOString(),
          updatedAt: account?.updated_at || new Date().toISOString(),
        });
      } catch (error) {
        console.error("Failed to fetch user account:", error);
        setCustomer({
          id: user.id,
          customerRegNo: user.username || "N/A",
          fullName: user.username || "User",
          emailAddress: user.email || "user@example.com",
          phoneNumber: "+255 700 000 000",
          position: user.role || "User",
          status: "ACTIVE",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user, userAccount, refreshUserAccount]);

  const validateForm = (formData) => {
    const newErrors = {};

    if (!formData.fullName?.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (formData) => {
    if (!validateForm(formData)) {
      toastError("Please fix all validation errors before continuing.");
      return;
    }

    try {
      setSaving(true);

      const updateData = {
        full_name: formData.fullName,
        phone: formData.phoneNumber,
      };

      const accountId = customer?.userId;

      if (!accountId) {
        throw new Error("User account ID not found");
      }

      const response = await userService.update(accountId, updateData);

      if (response?.status === "success") {
        setCustomer({
          ...customer,
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,
          updatedAt: new Date().toISOString(),
        });
        await refreshUserAccount(undefined, true);

        setIsEditing(false);
        toastSuccess("Profile updated successfully!");
      } else {
        throw new Error(response?.message || "Update failed");
      }
    } catch (error) {
      toastError(
        error?.message || "Failed to update profile. Please try again.",
      );
      console.error("Profile update error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const getUserRole = () => {
    if (!customer) return "";
    const roleMap = {
      super_admin: "Super Admin",
      admin: "Administrator",
      owner: "Shop Owner",
      seller: "Shop Seller",
    };
    return roleMap[customer.position] || customer.position || "User";
  };

  const ProfileStat = ({ icon, label, value, color }) => (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div
        className={`w-10 h-10 rounded-full ${color} flex items-center justify-center shrink-0`}
      >
        <FontAwesomeIcon icon={icon} className="text-white h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wider">
          {label}
        </p>
        <p className="text-sm font-semibold text-blue-700 truncate">{value}</p>
      </div>
    </div>
  );

  const InfoRow = ({
    icon,
    label,
    value,
    isEditable = false,
    name = "",
    onChange,
    error,
    formData,
  }) => (
    <div className="flex flex-col gap-0.5">
      <Label className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
        <FontAwesomeIcon icon={icon} className="text-blue-800 h-3 w-3" />
        {label}
      </Label>
      {isEditable ? (
        <Input
          name={name}
          value={formData[name] || ""}
          onChange={onChange}
          className={`border ${error ? "border-red-500" : "border-gray-300"} rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
        />
      ) : (
        <p className="text-sm font-medium text-blue-700">{value || "-"}</p>
      )}
      {error && (
        <p className="text-red-500 text-xs flex items-center gap-1 mt-0.5">
          <span className="w-1 h-1 bg-red-500 rounded-full"></span>
          {error}
        </p>
      )}
    </div>
  );

  // Edit form component with its own state
  const EditForm = ({ customer, onSave, onCancel, saving }) => {
    const [formData, setFormData] = useState({
      fullName: customer.fullName || "",
      phoneNumber: customer.phoneNumber || "",
    });
    const [localErrors, setLocalErrors] = useState({});

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (localErrors[name]) {
        setLocalErrors((prev) => ({ ...prev, [name]: "" }));
      }
    };

    const handleSubmit = () => {
      onSave(formData);
    };

    return (
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoRow
            icon={faUser}
            label="Full Name *"
            value={formData.fullName}
            isEditable={true}
            name="fullName"
            onChange={handleChange}
            error={localErrors.fullName}
            formData={formData}
          />
          <InfoRow
            icon={faEnvelope}
            label="Email Address"
            value={customer.emailAddress}
            isEditable={false}
          />
          <InfoRow
            icon={faPhone}
            label="Phone Number *"
            value={formData.phoneNumber}
            isEditable={true}
            name="phoneNumber"
            onChange={handleChange}
            error={localErrors.phoneNumber}
            formData={formData}
          />
          <InfoRow
            icon={faBuilding}
            label="Position / Role"
            value={getUserRole()}
            isEditable={false}
          />
        </div>

        <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <Label className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
            <FontAwesomeIcon
              icon={faIdBadge}
              className="text-gray-400 h-3 w-3"
            />
            Registration number
          </Label>
          <p className="text-sm font-medium text-blue-700 mt-2">
            {customer.customerRegNo}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="gap-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
            disabled={saving}
          >
            <FontAwesomeIcon icon={faTimes} />
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            className="gap-1 bg-blue-800 hover:bg-blue-900 text-white"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Save changes
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 text-sm">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <Card className="bg-white border border-gray-200 shadow-sm p-8 text-center">
        <p className="text-gray-600">No profile data available.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-800 to-blue-600 flex items-center justify-center shadow-lg">
              <FontAwesomeIcon
                icon={faUserCircle}
                className="text-white h-8 w-8"
              />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                My profile
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                View and manage your personal information
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`${statusStyles[customer.status]} border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5`}
            >
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${statusDotStyles[customer.status]}`}
              />
              {customer.status}
            </Badge>
            {!isEditing && (
              <Button
                onClick={() => setIsEditing(true)}
                className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
              >
                <FontAwesomeIcon icon={faEdit} />
                Edit profile
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Responsive grid - Personal details on top for small screens */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Personal details - First on small screens (top), third on large screens */}
        <div className="lg:col-span-2 order-1 lg:order-2">
          <Card className="bg-white border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FontAwesomeIcon
                  icon={FaRegAddressCard}
                  className="text-blue-800 h-4 w-4"
                />
                Personal details
              </h3>
            </div>

            {isEditing ? (
              <EditForm
                customer={customer}
                onSave={handleSave}
                onCancel={handleCancel}
                saving={saving}
              />
            ) : (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoRow
                    icon={faUser}
                    label="Full Name"
                    value={customer.fullName}
                  />
                  <InfoRow
                    icon={faEnvelope}
                    label="Email Address"
                    value={customer.emailAddress}
                  />
                  <InfoRow
                    icon={faPhone}
                    label="Phone Number"
                    value={customer.phoneNumber}
                  />
                  <InfoRow
                    icon={faBuilding}
                    label="Position / Role"
                    value={getUserRole()}
                  />
                </div>

                <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="text-xs text-gray-700 font-medium flex items-center gap-1.5">
                    <FontAwesomeIcon
                      icon={faIdBadge}
                      className="text-green-600 h-3 w-3"
                    />
                    Registration number
                  </Label>
                  <p className="text-sm font-medium text-blue-700">
                    {customer.customerRegNo}
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Account Information - Last on small screens (bottom), first on large screens */}
        <div className="lg:col-span-1 order-2 lg:order-1">
          <Card className="bg-white border border-gray-200 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FontAwesomeIcon
                icon={faIdBadge}
                className="text-blue-800 h-4 w-4"
              />
              Account Information
            </h3>

            <div className="flex flex-col gap-3">
              <ProfileStat
                icon={faIdBadge}
                label="Registration No."
                value={customer.customerRegNo}
                color="bg-purple-600"
              />

              <ProfileStat
                icon={faCalendarAlt}
                label="Member Since"
                value={formatDateTime(customer.createdAt)}
                color="bg-green-600"
              />

              <ProfileStat
                icon={faClock}
                label="Last Updated"
                value={formatDateTime(customer.updatedAt)}
                color="bg-amber-600"
              />

              <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm text-green-700 font-medium flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faCheckCircle} className="h-3 w-3" />
                  Verified account
                </p>
                <p className="text-[12px] text-slate-700 mt-0.5 font-semibold italic">
                  Your account is fully verified and active
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
